import { ArrowLeft, Play } from 'lucide-react';
import { GolfButton } from '@/components/golf/golf-button';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';
import type { TrainingDrill } from '@/lib/training-program/drill-loader';

interface StartDrillProps {
  drill: TrainingDrill;
  onBack: () => void;
  onStartExecution: (drillId: string) => void;
}

const formatAssignedReps = (drill: TrainingDrill): string => {
  const { sets, reps_per_set, rest_seconds } = drill.assigned_reps;
  if (sets > 0 && reps_per_set > 0) {
    const rest = rest_seconds > 0 ? ` • Rest ${rest_seconds}s` : '';
    return `${sets} sets of ${reps_per_set}${rest}`;
  }
  return 'Reps will be assigned in your plan.';
};

export function StartDrill({ drill, onBack, onStartExecution }: StartDrillProps) {
  const quickReminders = drill.what_to_look_for.slice(0, 3);
  const commonMistakes = (drill.common_mistakes || []).slice(0, 2);

  // UX-AUDIT-V3-203: pb-40 to ensure bottom nav is accessible below sticky CTA
  return (
    <div className="min-h-screen bg-background pb-40">
      {/* UX-AUDIT-V3-300: Dark green gradient header matching other screens */}
      {/* UX-AUDIT-V3-301: "Ready to Start" label above drill name */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <p className="text-sm text-white/70">Ready to Start</p>
              <h1 className="text-2xl font-semibold">{drill.drill_name}</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm text-white/80 mt-3">
            {drill.category && (
              <span className="px-3 py-1 rounded-full bg-white/10 capitalize">
                {drill.category.replace('_', ' ')}
              </span>
            )}
            {drill.duration_minutes ? (
              <span className="px-3 py-1 rounded-full bg-white/10">{drill.duration_minutes} min</span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* UX-AUDIT-V3-302: Swing Thought as HERO element - largest text, centered, with decorative glow */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0D382A] via-[#196644] to-[#0D382A] text-white p-8">
          {/* Decorative glow/pattern background */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-[radial-gradient(circle,rgba(249,201,78,0.2),transparent_70%)]" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[radial-gradient(circle,rgba(255,255,255,0.1),transparent_70%)]" />
          
          <div className="relative z-10 text-center space-y-3">
            <p className="text-sm uppercase tracking-widest text-white/70">Keep This in Mind</p>
            <p className="text-2xl md:text-3xl font-semibold italic leading-relaxed">
              {drill.swing_thought || 'Stay focused on your swing thought.'}
            </p>
          </div>
        </div>

        <GolfCard className="bg-[#F9C94E]/10 border-[#F9C94E]/30">
          <GolfCardHeader>
            <GolfCardTitle>Assigned Reps</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <p className="text-sm text-foreground">{formatAssignedReps(drill)}</p>
          </GolfCardContent>
        </GolfCard>

        {quickReminders.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Quick Reminders</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <ul className="space-y-2">
                {quickReminders.map((item, index) => (
                  <li key={index} className="text-sm text-foreground">• {item}</li>
                ))}
              </ul>
            </GolfCardContent>
          </GolfCard>
        )}

        {commonMistakes.length > 0 && (
          <GolfCard className="bg-red-50 border-red-100">
            <GolfCardHeader>
              <GolfCardTitle>Common Mistakes</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <ul className="space-y-2">
                {commonMistakes.map((item, index) => (
                  <li key={index} className="text-sm text-foreground">• {item}</li>
                ))}
              </ul>
            </GolfCardContent>
          </GolfCard>
        )}
      </div>

      {/* UX-AUDIT-V3-303: Play icon on Start Drill button */}
      {/* UX-AUDIT-V3-203: Position sticky CTA above bottom nav (bottom-16 = 64px) */}
      <div className="sticky bottom-16 left-0 right-0 z-40 bg-background border-t border-border p-4">
        <GolfButton
          onClick={() => onStartExecution(drill.drill_id)}
          className="w-full gap-2"
          size="lg"
        >
          <Play className="w-5 h-5" />
          Start Drill
        </GolfButton>
      </div>
    </div>
  );
}
